import { z } from 'zod/v4'
import { SentimentEnum } from '@/api/workspaces-api'
import { ownerSchema } from '@/types/schema/api/owner-schema'

export type InsightReportListRequest = {
  page: number
  per_page?: number
  name?: string
  owner_ids?: string // e.g., `444777` or `444777,444778`
  sort_field?: string
  sort_order?: 'asc' | 'desc'
}

export const insightReportListReportSchema = z.object({
  id: z.number(),
  name: z.string(),
  start_at: z.iso.datetime(),
  end_at: z.iso.datetime(),
  budget: z.number(),
  kol_count: z.number(),
  owner: ownerSchema.optional(),
  snapshot_at: z.iso.datetime().optional(),
})

export type InsightReportListReport = z.infer<
  typeof insightReportListReportSchema
>

export const insightReportListResponseSchema = z.object({
  data: insightReportListReportSchema.array(),
  page: z.number(),
  per_page: z.number(),
  total: z.number(),
})

export type InsightReportListResponse = z.infer<
  typeof insightReportListResponseSchema
>

export const insightReportOwnersResponseSchema = z.object({
  data: ownerSchema.array(),
})

export type InsightReportOwnersResponse = z.infer<
  typeof insightReportOwnersResponseSchema
>

type ReportId = number | 'demo'

type BasicInsightReportRequest = {
  report_id: ReportId
}

export type TriggerCrawlersRequest = BasicInsightReportRequest

export type UpdateCommentTermsRequest = BasicInsightReportRequest & {
  term: string
  is_included: boolean
}

export type CreateInsightReportRequest = {
  name: string
  owner_id: number
  budget: number
  start_at: string
  end_at: string
  keywords: string[]
  kol_uuids: string[]
}

export const createInsightReportResponseSchema = z.object({
  id: z.number(),
})

export type CreateInsightReportResponse = z.infer<
  typeof createInsightReportResponseSchema
>

export type UpdateInsightReportRequest = BasicInsightReportRequest & {
  name: string
  owner_id: number
  budget: number
  start_at: string
  end_at: string
  keywords: string[]
  kol_uuids: string[]
}

export type DeleteInsightReportRequest = BasicInsightReportRequest

export type InsightReportOverviewRequest = BasicInsightReportRequest

const overviewAiSchema = z.object({
  quota: z.number(),
  summaries: z.string().array(),
})

export const crawlerPlatformSchema = z.enum([
  'facebook',
  'instagram',
  'youtube',
])
export type CrawlerPlatform = z.infer<typeof crawlerPlatformSchema>
const platformSchema = crawlerPlatformSchema.or(z.string())
export type Platform = z.infer<typeof platformSchema>

export const crawlerPostTypeSchema = z.enum([
  'post',
  'video',
  'live',
  'story',
  'shorts',
  'ad_authorization', // 廣告授權
  'material_authorization', // 素材授權
])
export type CrawlerPostType = z.infer<typeof crawlerPostTypeSchema>
const postTypeSchema = crawlerPostTypeSchema.or(z.string())
export type PostType = z.infer<typeof postTypeSchema>

const overviewKolSchema = z.object({
  id: z.number(),
  uuid: z.uuid(),
  name: z.string(),
  is_verified: z.boolean(),
  is_radar_star: z.boolean(),
  total_follower_count: z.number(),
  platforms: z
    .object({
      platform: crawlerPlatformSchema,
      follower_count: z.number(),
    })
    .array(),
})
export type OverviewKol = z.infer<typeof overviewKolSchema>

const customPlatformAndPostTypeSchema = z.object({
  is_custom_platform: z.literal(true),
  platform: z.string(),
  is_custom_post_type: z.literal(true),
  post_type: z.string(),
})

const predefinedPlatformCustomPostTypeSchema = z.object({
  is_custom_platform: z.literal(false),
  platform: crawlerPlatformSchema,
  is_custom_post_type: z.literal(true),
  post_type: z.string(),
})

const predefinedPlatformAndPostTypeSchema = z.object({
  is_custom_platform: z.literal(false),
  platform: crawlerPlatformSchema,
  is_custom_post_type: z.literal(false),
  post_type: crawlerPostTypeSchema,
})

const postWithPostTypeSchema = z.union([
  customPlatformAndPostTypeSchema,
  predefinedPlatformCustomPostTypeSchema,
  predefinedPlatformAndPostTypeSchema,
])

export type PostWithPostType = z.infer<typeof postWithPostTypeSchema>

const overviewPostSchema = z
  .object({
    id: z.number(),
    kol_uuid: z.uuid(),
    published_at: z.iso.datetime().optional(),
    like_count: z.number().nullish(),
    comment_count: z.number().nullish(),
    share_count: z.number().nullish(),
    engagement_count: z.number().nullish(),
    view_count: z.number().nullish(),
    reach_count: z.number().nullish(),
    click_count: z.number().nullish(),
    positive_comment_count: z.number().nullish(),
    negative_comment_count: z.number().nullish(),
  })
  .and(postWithPostTypeSchema)

export type OverviewPost = z.infer<typeof overviewPostSchema>

export const insightReportOverviewSchema = z.object({
  data: z.object({
    id: z.number(),
    name: z.string(),
    budget: z.number(),
    start_at: z.iso.datetime(),
    end_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    snapshot_at: z.iso.datetime().optional(),
    last_crawler_triggered_at: z.iso.datetime().optional(),
    last_kol_modified_at: z.iso.datetime().optional(),
    last_crawler_status: z.enum(['running', 'succeeded', 'failed']).nullish(),
    keywords: z.string().array(),
    total_engagement_count: z.number().nullish(),
    total_reach_count: z.number().nullish(),
    cpe: z.number().nullish(),
    cpv: z.number().nullish(),
    cpev: z.number().nullish(),
    womm: z.number().nullish(),
    kols: overviewKolSchema.array(),
    posts: overviewPostSchema.array(),
    ai: overviewAiSchema.nullish(),
    owner: ownerSchema.nullish(),
  }),
})

export type InsightReportOverviewResponse = z.infer<
  typeof insightReportOverviewSchema
>

export type SortField = 'published_at' | 'kol_name' | 'engagement_count'

export type InsightReportPostsRequest = BasicInsightReportRequest & {
  page: number
  per_page?: number
  platforms: CrawlerPlatform[]
  custom_platforms: string[]
  post_types: CrawlerPostType[]
  custom_post_types: string[]
  kol_uuids: string[]
  sort_field: SortField
}

const insightReportPostKolSchema = z.object({
  name: z.string(),
  uuid: z.uuid(),
  is_radar_star: z.boolean(),
  is_verified: z.boolean(),
})

export type InsightReportPostKol = z.infer<typeof insightReportPostKolSchema>

const insightReportPostSchema = z
  .object({
    kol: insightReportPostKolSchema,
    id: z.number(),
    source_post_id: z.string().optional(),
    platform_user_id: z.string().optional(),
    thumbnail_url: z.string().optional(),
    published_at: z.iso.datetime().optional(),
    content: z.string(),
    origin_url: z.string().optional(),
    is_handfilled: z.boolean(),
    is_collection_post: z.boolean(),
    like_count: z.number().optional(),
    comment_count: z.number().optional(),
    view_count: z.number().optional(),
    play_count: z.number().optional(),
    share_count: z.number().optional(),
    click_count: z.number().optional(),
  })
  .and(postWithPostTypeSchema)

export type InsightReportPost = z.infer<typeof insightReportPostSchema>

export const insightReportInfinitePostsSchema = z.object({
  data: insightReportPostSchema.array(),
  page: z.number(),
  per_page: z.number(),
  total: z.number(),
})

export type InsightReportInfinitePostsResponse = z.infer<
  typeof insightReportInfinitePostsSchema
>

export type InsightReportKolPerformanceRequest = BasicInsightReportRequest & {
  platforms?: CrawlerPlatform[]
  custom_platforms?: string[]
  post_types?: CrawlerPostType[]
  custom_post_types?: string[]
  kol_uuids?: string[]
}

export type InsightReportKolAnalysisRequest = BasicInsightReportRequest & {
  platform?: CrawlerPlatform
  custom_platforms?: string[]
  post_types?: CrawlerPostType[]
  custom_post_types?: string[]
  kol_uuids?: string[]
}

export const insightReportKolPerformanceDataSchema = z.object({
  data: z.object({
    post_count: z.number(),
    like_count: z.number().nullish(),
    comment_count: z.number().nullish(),
    share_count: z.number().nullish(),
    engagement_count: z.number().nullish(),
    engagement_rate: z.number().nullish(),
    view_count: z.number().nullish(),
    view_rate: z.number().nullish(),
    reach_count: z.number().nullish(),
    reach_rate: z.number().nullish(),
    click_count: z.number().nullish(),
    click_rate: z.number().nullish(),
  }),
})

type InsightReportKolPerformance = z.infer<
  typeof insightReportKolPerformanceDataSchema
>
export type InsightReportKolPerformanceResponse = InsightReportKolPerformance

export type InsightReportCommentAnalysisRequest = BasicInsightReportRequest & {
  platform?: CrawlerPlatform
  post_types?: CrawlerPostType[]
  kol_uuids?: string[]
}

export const wordSchema = z.object({
  word: z.string(),
  count: z.number(),
  is_included: z.boolean(),
})

export type Word = z.infer<typeof wordSchema>

const wordCloudSchema = z.object({
  ai_summary: z.string(),
  words: wordSchema.array(),
})

export type WordCloud = z.infer<typeof wordCloudSchema>

const kolSentimentSchema = z.object({
  name: z.string(),
  uuid: z.uuid(),
  is_radar_star: z.boolean(),
  is_verified: z.boolean(),
  positive: z.number(),
  negative: z.number(),
  neutral: z.number(),
  unknown: z.number(),
})
export type KolSentiment = z.infer<typeof kolSentimentSchema>

export const insightReportCommentAnalysisSchema = z.object({
  data: z.object({
    kol_sentiments: kolSentimentSchema.array(),
    word_cloud: wordCloudSchema,
  }),
})

export type InsightReportCommentAnalysisResponse = z.infer<
  typeof insightReportCommentAnalysisSchema
>

export type InsightReportCustomPostTypesRequest = BasicInsightReportRequest

export const insightReportCustomPostTypesSchema = z.object({
  data: postWithPostTypeSchema.array(),
})
export type InsightReportCustomPostTypesResponse = z.infer<
  typeof insightReportCustomPostTypesSchema
>

export type InsightReportCommentsRequest = BasicInsightReportRequest & {
  platform?: CrawlerPlatform
  post_types?: CrawlerPostType[]
  kol_uuids?: string[]
  sentiment?: SentimentEnum
  keyword?: string
  page: number
  per_page: number
}

const commentSchema = z.object({
  id: z.number(),
  content: z.string(),
  published_at: z.iso.datetime(),
  kol: insightReportPostKolSchema,
  author_name: z.string(),
  origin_url: z.url(),
})

export const insightReportCommentsSchema = z.object({
  data: commentSchema.array(),
  page: z.number(),
  per_page: z.number(),
  total: z.number(),
})

export type InsightReportCommentsResponse = z.infer<
  typeof insightReportCommentsSchema
>

export type AddKolsToInsightReportRequest = BasicInsightReportRequest & {
  kol_uuids: string[]
}

export type RemoveKolsFromInsightReportRequest = BasicInsightReportRequest & {
  kol_uuid: string
}

export type DeleteInsightReportHandFilledPostRequest =
  BasicInsightReportRequest & {
    post_id: number
  }

const kolRankingKolSchema = insightReportPostKolSchema.extend({
  value: z.number().optional(),
})

export type KolRankingKol = z.infer<typeof kolRankingKolSchema>

export const kolRankingSchema = z.object({
  by_like_count: z.array(kolRankingKolSchema),
  by_comment_count: z.array(kolRankingKolSchema),
  by_engagement_count: z.array(kolRankingKolSchema),
  by_engagement_rate: z.array(kolRankingKolSchema),
  by_view_count: z.array(kolRankingKolSchema),
  by_view_rate: z.array(kolRankingKolSchema),
  by_reach_count: z.array(kolRankingKolSchema),
  by_reach_rate: z.array(kolRankingKolSchema),
  by_click_count: z.array(kolRankingKolSchema),
  by_click_rate: z.array(kolRankingKolSchema),
})

const kolRankingKeys = kolRankingSchema.keyof()
export type KolRankingKey = z.infer<typeof kolRankingKeys>

const customRankingPostPlatformSchema = z.object({
  is_custom_platform: z.literal(true),
  platform: z.string(),
  platform_user_id: z.undefined(),
  source_post_id: z.undefined(),
  origin_url: z.url().optional(),
})

const builtInRankingPostPlatformSchema = z.object({
  is_custom_platform: z.literal(false),
  platform: crawlerPlatformSchema,
  platform_user_id: z.string().optional(),
  source_post_id: z.string().optional(),
  origin_url: z.url().optional(),
})

const rankingPostPlatformSchema = z.union([
  customRankingPostPlatformSchema,
  builtInRankingPostPlatformSchema,
])

const postRankingPostSchema = z
  .object({
    content: z.string(),
    thumbnail_url: z.string().optional(),
    author: insightReportPostKolSchema,
    value: z.number().optional(),
  })
  .and(rankingPostPlatformSchema)

export type PostRankingPost = z.infer<typeof postRankingPostSchema>

const postRankingSchema = z.object({
  by_like_count: z.array(postRankingPostSchema),
  by_comment_count: z.array(postRankingPostSchema),
  by_engagement_count: z.array(postRankingPostSchema),
  by_engagement_rate: z.array(postRankingPostSchema),
  by_view_count: z.array(postRankingPostSchema),
  by_view_rate: z.array(postRankingPostSchema),
  by_reach_count: z.array(postRankingPostSchema),
  by_reach_rate: z.array(postRankingPostSchema),
  by_click_count: z.array(postRankingPostSchema),
  by_click_rate: z.array(postRankingPostSchema),
})

export type PostRanking = z.infer<typeof postRankingSchema>

const followerLevelSchema = z.object({
  value: z.number().optional(),
  level: z.literal([
    'nano',
    'micro',
    'small',
    'mid-small',
    'medium',
    'mid-large',
    'large',
    'million',
  ]),
})

const followerLevelRankingSchema = z.object({
  by_like_count: z.array(followerLevelSchema),
  by_comment_count: z.array(followerLevelSchema),
  by_engagement_count: z.array(followerLevelSchema),
  by_engagement_rate: z.array(followerLevelSchema),
  by_view_count: z.array(followerLevelSchema),
  by_view_rate: z.array(followerLevelSchema),
  by_reach_count: z.array(followerLevelSchema),
  by_reach_rate: z.array(followerLevelSchema),
  by_click_count: z.array(followerLevelSchema),
  by_click_rate: z.array(followerLevelSchema),
})

export type FollowerLevelRanking = z.infer<typeof followerLevelRankingSchema>

const SubTagSchema = z.object({
  name: z.string(),
  value: z.number().optional(),
})

const customizedTagRankingDataSchema = z.object({
  name: z.string(),
  sub_tags: z.array(SubTagSchema),
  value: z.number().optional(),
})

const customizedTagRankingSchema = z.object({
  by_like_count: z.array(customizedTagRankingDataSchema),
  by_comment_count: z.array(customizedTagRankingDataSchema),
  by_engagement_count: z.array(customizedTagRankingDataSchema),
  by_engagement_rate: z.array(customizedTagRankingDataSchema),
  by_view_count: z.array(customizedTagRankingDataSchema),
  by_view_rate: z.array(customizedTagRankingDataSchema),
  by_reach_count: z.array(customizedTagRankingDataSchema),
  by_reach_rate: z.array(customizedTagRankingDataSchema),
  by_click_count: z.array(customizedTagRankingDataSchema),
  by_click_rate: z.array(customizedTagRankingDataSchema),
})

export type CustomizedTagRanking = z.infer<typeof customizedTagRankingSchema>

const averageSchema = z.object({
  like_count: z.number().optional(),
  comment_count: z.number().optional(),
  engagement_count: z.number().optional(),
  engagement_rate: z.number().optional(),
  view_count: z.number().optional(),
  view_rate: z.number().optional(),
  reach_count: z.number().optional(),
  reach_rate: z.number().optional(),
  click_count: z.number().optional(),
  click_rate: z.number().optional(),
})

const kolAnalysisAiSummary = z.object({
  metrics: z.literal([
    'by_like_count',
    'by_comment_count',
    'by_engagement_count',
    'by_engagement_rate',
    'by_view_count',
    'by_view_rate',
    'by_reach_count',
    'by_reach_rate',
    'by_click_count',
    'by_click_rate',
  ]),
  summary: z.string(),
})

export const insightReportKolAnalysisSchema = z.object({
  data: z.object({
    average: averageSchema,
    post_ranking: postRankingSchema,
    kol_ranking: kolRankingSchema,
    customized_tag_ranking: customizedTagRankingSchema,
    follower_level_ranking: followerLevelRankingSchema,
    ai_summary: kolAnalysisAiSummary.array(),
  }),
})

export type InsightReportKolAnalysisResponse = z.infer<
  typeof insightReportKolAnalysisSchema
>

const insightReportKolPostSchema = z
  .object({
    id: z.number(),
    budget: z.number().optional(),
    thumbnail_url: z.string().optional(),
    source_post_id: z.string().optional(),
    platform_user_id: z.string().optional(),
    origin_url: z.string().optional(),
    content: z.string().optional(),
    is_handfilled: z.boolean(),
    is_hidden: z.boolean(),
    is_disable_like: z.boolean(),
    use_handfilled: z.boolean(),
    like_count: z.number().optional(),
    comment_count: z.number().optional(),
    published_at: z.iso.datetime().optional(),
    share_count: z.number().optional(),
    view_count: z.number().optional(),
    reach_count: z.number().optional(),
    click_count: z.number().optional(),
    handfilled: z.object({
      like_count: z.number().optional(),
      comment_count: z.number().optional(),
      share_count: z.number().optional(),
      view_count: z.number().optional(),
      reach_count: z.number().optional(),
      click_count: z.number().optional(),
    }),
  })
  .and(postWithPostTypeSchema)

export type InsightReportKolPost = z.infer<typeof insightReportKolPostSchema>

export const insightReportKolSchema = insightReportPostKolSchema.extend({
  id: z.number(),
  created_at: z.iso.datetime(),
  total_follower_count: z.number(),
  total_post_budget: z.number(),
  posts: insightReportKolPostSchema.array(),
})

export type InsightReportKol = z.infer<typeof insightReportKolSchema>

export const insightReportKolsSchema = z.object({
  kols: insightReportKolSchema.array(),
  page: z.number(),
  per_page: z.number(),
  total: z.number(),
})

export type InsightReportKolsResponse = z.infer<typeof insightReportKolsSchema>

export type FetchInsightReportKolsRequest = BasicInsightReportRequest & {
  kol_name?: string
  page: number
  per_page: number
}

export type CreateInsightReportPostRequest = BasicInsightReportRequest & {
  kol_uuid: string
  published_at?: string
  budget?: number
  like_count?: number
  comment_count?: number
  share_count?: number
  engagement_count?: number
  view_count?: number
  reach_count?: number
  click_count?: number
  thumbnail_url?: string
  is_custom_platform: boolean
  platform: string
  is_custom_post_type: boolean
  post_type: string
}

type UpdateInsightReportPostBasicRequest = BasicInsightReportRequest &
  Pick<InsightReportKolPost, 'id'>
type UpdateInsightReportPostHiddenRequest =
  UpdateInsightReportPostBasicRequest & Pick<InsightReportKolPost, 'is_hidden'>
export type DatacenterHandFilled = Pick<
  InsightReportKolPost,
  | 'like_count'
  | 'comment_count'
  | 'share_count'
  | 'view_count'
  | 'reach_count'
  | 'click_count'
>
export type NonDatacenterHandFilled = Pick<
  InsightReportKolPost,
  | 'like_count'
  | 'comment_count'
  | 'share_count'
  | 'view_count'
  | 'reach_count'
  | 'click_count'
  | 'thumbnail_url'
>
type UpdateInsightReportDatacenterPostBasicRequest =
  | (UpdateInsightReportPostBasicRequest & {
      budget: number
      use_handfilled: true
      handfilled: DatacenterHandFilled
    })
  | {
      budget: number
      use_handfilled: false
    }
type UpdateInsightReportNonDatacenterPostBasicRequest =
  UpdateInsightReportPostBasicRequest & {
    budget: number
    published_at: string
    handfilled: DatacenterHandFilled
  }
type UpdateInsightReportDatacenterPostRequest =
  UpdateInsightReportPostBasicRequest &
    UpdateInsightReportDatacenterPostBasicRequest
type UpdateInsightReportNonDatacenterPostRequest =
  UpdateInsightReportPostBasicRequest &
    UpdateInsightReportNonDatacenterPostBasicRequest

export type UpdateInsightReportPostRequest =
  | UpdateInsightReportPostHiddenRequest
  | UpdateInsightReportDatacenterPostRequest
  | UpdateInsightReportNonDatacenterPostRequest

export type TriggerOverviewAiSummaryRequest = BasicInsightReportRequest

export type ExportInsightReportPptxRequest = BasicInsightReportRequest & {
  conditions: {
    platforms: ('facebook' | 'instagram' | 'youtube')[]
    custom_platforms: string[]
  }
  pptx: {
    kol_analysis: {
      slides: (
        | 'kol_ranking'
        | 'customized_tag_ranking'
        | 'follower_level_ranking'
        | 'top5_engagement_posts'
        | 'top5_view_posts'
      )[]
      metrics: (
        | 'like_count'
        | 'comment_count'
        | 'share_count'
        | 'engagement_count'
        | 'engagement_rate'
        | 'view_count'
        | 'view_rate'
        | 'reach_count'
        | 'reach_rate'
        | 'click_count'
        | 'click_rate'
      )[]
      others: ('ai_summary' | 'report_avg')[]
    }
    comment_analysis: {
      slides: (
        | 'word_cloud'
        | 'comment_sentiment'
        | 'kol_sentiment_rate'
        | 'kol_sentiment_ranking'
        | 'comments'
      )[]
      others: 'ai_summary'[]
    }
    posts: {
      slides: ('metrics' | 'posts')[]
      metrics: (
        | 'like_count'
        | 'comment_count'
        | 'share_count'
        | 'engagement_count'
        | 'engagement_rate'
        | 'view_count'
        | 'view_rate'
        | 'reach_count'
        | 'reach_rate'
        | 'click_count'
        | 'click_rate'
      )[]
    }
  }
}

export const exportPptxResponseSchema = z.object({})
export type ExportPptxResponse = z.infer<typeof exportPptxResponseSchema>
